// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { useCallback, useEffect, useState } from "react";
import {
    WanikaniAuthError,
    fetchAllAssignments,
    fetchAllSubjects,
    fetchSummary,
    validateToken,
} from "../utils/wanikaniClient";
import { buildProgressByKanji } from "../utils/wanikaniProgress";
import * as analytics from "../utils/analytics";
import {
    clearCache,
    clearToken,
    getCache,
    getToken,
    isSubjectsCacheStale,
    setCache,
    setToken,
} from "../utils/wanikaniStorage";

const initialState = {
    status: "disconnected", // disconnected | loading | ready | error
    error: null,
    level: null,
    lessonsAvailable: null,
    reviewsAvailable: null,
    progressByKanji: {},
    isSyncing: false,
};

function hydrateFromCache(cache) {
    if (!cache) return null;
    return {
        status: "ready",
        error: null,
        level: cache.user?.level ?? null,
        lessonsAvailable: null,
        reviewsAvailable: null,
        progressByKanji: buildProgressByKanji(cache.subjects || [], cache.assignments || []),
        isSyncing: false,
    };
}

export function useWanikaniProgress() {
    const [state, setState] = useState(initialState);

    const sync = useCallback(async (token) => {
        setState((current) => ({ ...current, status: current.status === "ready" ? "ready" : "loading", isSyncing: true, error: null }));

        try {
            const cache = getCache() || {};
            const [user, summary, assignments] = await Promise.all([
                validateToken(token),
                fetchSummary(token),
                fetchAllAssignments(token),
            ]);

            let subjects = cache.subjects;
            let subjectsFetchedAt = cache.subjectsFetchedAt;
            if (isSubjectsCacheStale(subjectsFetchedAt) || !subjects?.length) {
                subjects = await fetchAllSubjects(token);
                subjectsFetchedAt = Date.now();
            }

            const nextCache = {
                user: user.data,
                subjects,
                subjectsFetchedAt,
                assignments,
                assignmentsFetchedAt: Date.now(),
            };
            setCache(nextCache);

            const lessonsAvailable = summary.data.lessons?.[0]?.subject_ids?.length ?? 0;
            const reviewsAvailable = summary.data.reviews?.[0]?.subject_ids?.length ?? 0;

            setState({
                status: "ready",
                error: null,
                level: user.data.level,
                lessonsAvailable,
                reviewsAvailable,
                progressByKanji: buildProgressByKanji(subjects, assignments),
                isSyncing: false,
            });
            // Returned so connect() can tell a genuinely successful sync
            // apart from a failed one, to fire wani_kani_connected only on
            // an actual new connection - see connect() below.
            return user.data.level;
        } catch (error) {
            if (error instanceof WanikaniAuthError) {
                clearToken();
                clearCache();
                setState({ ...initialState, status: "error", error: "That WaniKani token isn't valid. Please generate a new one and try again." });
                return null;
            }

            setState((current) => ({
                ...current,
                status: current.progressByKanji && Object.keys(current.progressByKanji).length ? "ready" : "error",
                error: "Couldn't reach WaniKani right now. Please try again shortly.",
                isSyncing: false,
            }));
            return null;
        }
    }, []);

    useEffect(() => {
        const token = getToken();
        if (!token) return;

        const cached = hydrateFromCache(getCache());
        if (cached) setState(cached);

        sync(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const connect = useCallback(
        async (token) => {
            const trimmed = token.trim();
            if (!trimmed) return;
            setToken(trimmed);
            const level = await sync(trimmed);
            // Only fires here (a genuine new connection via the connect
            // form), never from the useEffect hydration sync() or refresh()
            // - both of those call sync() directly, not connect().
            if (level !== null) {
                analytics.track("wani_kani_connected", { wanikani_level: level });
            }
        },
        [sync]
    );

    const disconnect = useCallback(() => {
        clearToken();
        clearCache();
        setState(initialState);
    }, []);

    const refresh = useCallback(() => {
        const token = getToken();
        if (token) sync(token);
    }, [sync]);

    return { ...state, connect, disconnect, refresh };
}
